import type { HoleScoreFragment } from '../../../__generated__/hooks'
import { termFor } from '../../../util/term'
import { FILLED_TONES } from './marks'
import ScoreMark from './ScoreMark'
import StrokeDots from './StrokeDots'

/**
 * One score cell — the mark + the gross number + the optional
 * stroke-given red dot. Optional `subLabel` shows the contributing
 * player's initials in best-ball team view. When `showStableford` is
 * set (Stableford ranking / POLF) the hole's stableford points sit
 * under the mark — the individual-scorecard equivalent of the Pts
 * column on the mini scorecard. Suppressed when `subLabel` already
 * occupies that slot (best-ball team view).
 */
const ScoreCell = ({
  score,
  par,
  strokes,
  subLabel,
  showStableford,
}: {
  score: HoleScoreFragment | undefined
  par: number
  strokes: number
  /** Best-ball: contributor initials shown under the score cell. */
  subLabel?: string | null
  /** Stableford ranking → show the hole's stableford points under the mark. */
  showStableford?: boolean
}) => {
  if (!score || score.score == null) {
    // Even with no score the player may get a handicap stroke here — show the
    // red dot so an unplayed hole still tells you where your shots fall.
    return (
      <td className="px-1 py-1 text-center text-ink-300 font-mono num text-sm">
        <span className="relative inline-block">
          —
          <StrokeDots count={strokes} top={-2} right={-7} />
        </span>
      </td>
    )
  }
  const t = termFor(score.score, par)
  const size = 44
  // Filled marks need the number reversed to paper so it reads on the
  // dark/gold ground. Hollow marks (and par's no-mark) keep ink-900.
  const isFilled = FILLED_TONES.has(t.tone)
  const numberColor = isFilled ? 'var(--paper)' : 'var(--ink-900)'
  return (
    <td className="p-0">
      <div className="flex flex-col items-center justify-center min-h-12 py-1">
        <div
          className="relative inline-block"
          style={{ width: size, height: size }}
        >
          <ScoreMark tone={t.tone} size={size} />
          <span
            className="absolute inset-0 flex items-center justify-center font-mono num text-base font-semibold"
            style={{
              color: numberColor,
              letterSpacing: '-0.02em',
              lineHeight: 1,
              paddingTop: 1,
            }}
          >
            <span className="relative">
              {score.score}
              <StrokeDots count={strokes} top={-2} right={-7} />
            </span>
          </span>
        </div>
        {subLabel ? (
          <span
            className="mt-0.5 text-[9px] font-bold uppercase tracking-wider text-gold"
            aria-label={`Brukt: ${subLabel}`}
          >
            {subLabel}
          </span>
        ) : null}
        {!subLabel && showStableford && score.stablefordPoints != null ? (
          <span
            className="mt-0.5 text-[10px] font-bold text-green-800 num leading-none"
            aria-label={`${score.stablefordPoints} poeng`}
          >
            {score.stablefordPoints} p
          </span>
        ) : null}
      </div>
    </td>
  )
}

export default ScoreCell
